import {
    CalendarOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    CreditCardOutlined,
    ExclamationCircleFilled,
    FileExcelOutlined,
    SearchOutlined,
} from "@ant-design/icons";
import {
    Badge,
    Button,
    Card,
    Col,
    ConfigProvider,
    DatePicker,
    Form,
    Input,
    InputNumber,
    Modal,
    Row,
    Select,
    Space,
    Spin,
    Switch,
    Table,
    Tag,
    Tooltip,
    Typography,
} from "antd";
import frFR from "antd/locale/fr_FR";
import * as XLSX from "xlsx";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Footer from "../../components/Footer";
import NavBar from "../../components/NavBar";
import SideBar from "../../components/SideBar";
import { rightsService } from "../../services/rightsService";
import axiosInstance from "../../variables/api/axiosInstance";
import { LOGGED_USER } from "../../variables/constants";
import { frenchDate, frenchDateSimple } from "../../variables/functions";
import { displayNumbers } from "../../variables/numbersFunctions";
import dayjs from "dayjs";

const { confirm } = Modal;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text, Title } = Typography;

const STATUS_COLORS = {
    regle:    { bg: "#f6ffed", border: "#b7eb8f" },
    nonRegle: { bg: "#fffbe6", border: "#ffe58f" },
};

// v2 : la liste ne contient QUE des non-effectués (la fonction SQL filtre déjà
// reglements = 0) → on retire le filtre « Réglé/Non réglé » devenu inutile et on
// clarifie le titre. Aucun changement du flux d'encaissement.
const EnAttenteDEncaissement = ({ v2 = false } = {}) => {
    const [data, setData]                               = useState([]);
    const [loadingContrats, setLoadingContrats]         = useState(true);
    const [pagination, setPagination]                   = useState({ current: 1, pageSize: 10 });
    const [selecting, setSelecting]                     = useState(false);
    const [modalModePaiement, setModalModePaiement]     = useState(false);
    const [contractsToPay, setContractsToPay]           = useState([]);
    const [selectedRows, setSelectedRows]               = useState([]);
    const [selectedRowKeys, setSelectedRowKeys]         = useState([]);
    const [filteredInfo, setFilteredInfo]               = useState({});
    const [sortedInfo, setSortedInfo]                   = useState({});
    const [total, setTotal]                             = useState(0);
    const [membres, setMembres]                         = useState([]);
    const [selectedCompagnies, setSelectedCompagnies]   = useState([]);
    const [banques, setBanques]                         = useState([]);
    const [count, setCount]                             = useState({});
    const [selectedDate, setSelectedDate]               = useState(null);
    const [modePaiement, setModePaiement]               = useState("ESPECES");
    const [numeroPaiement, setNumeroPaiement]           = useState("");
    const [selectedContrat, setSelectedContrat]         = useState(null);
    const [currentPage, setCurrentPage]                 = useState(1);
    const [pageSize, setPageSize]                       = useState(10);
    const [banque, setBanque]                           = useState("");
    const [dateVirement, setDateVirement]               = useState(null);
    const [reference, setReference]                     = useState("");
    const [searchText, setSearchText]                   = useState("");
    const [modePaiementForm]                            = Form.useForm();
    // Encaissement PARTIEL (HLA093) : montant réellement encaissé ; défaut = prime totale (= plein).
    // Un partiel fait quitter le contrat de CETTE liste (1 règlement créé) ; le reste se suit dans /encaissements.
    const canEncaissementPartiel                        = rightsService("HLA093");
    const [montantEncaisse, setMontantEncaisse]         = useState(null);

    const navigate = useNavigate();

    /* ── Données triées : NON REGLE d'abord ── */
    const sortedData = [...data].sort((a, b) => {
        const aRegle = a.regle === "REGLE" ? 1 : 0;
        const bRegle = b.regle === "REGLE" ? 1 : 0;
        return aRegle - bRegle;
    });

    const filteredData = sortedData.filter((r) => {
        if (!searchText) return true;
        const q = searchText.toLowerCase();
        return (
            (r.souscripteur || "").toLowerCase().includes(q) ||
            (r.numeroPolice || "").toLowerCase().includes(q)
        );
    });

    /* ── Fetch ── */
    const fetchData = async (params = {}) => {
        setLoadingContrats(true);
        try {
            const response = await axiosInstance.get("/api/contrat/en-attente-encaissement", {
                params: {
                    page: (params.pagination?.current || 1) - 1,
                    size: params.pagination?.pageSize || 10,
                    ...params.filters,
                    compagnie: params.filters?.compagnie?.join(","),
                },
            });
            const { content, totalElements } = response.data;
            const ctrs = content.map((contrat, key) => ({
                key: contrat.codeId,
                codeId: contrat.codeId,
                id: key,
                numeroPolice: contrat.numeroPoliceM || contrat.numeroPoliceP,
                souscripteur: contrat?.prenom
                    ? `${contrat.prenom} ${contrat.nom}`
                    : contrat?.nom || "",
                organisme: contrat?.raisonSociale,
                dateEffet: contrat?.dateEffet ? frenchDate(contrat.dateEffet) : "",
                dateEcheance: contrat?.dateEcheance ? frenchDateSimple(contrat.dateEcheance) : "",
                regle: contrat.reglements ? "REGLE" : "NON REGLE",
                primeTotale: displayNumbers(contrat?.primeTotale),
                primeNette: displayNumbers(contrat?.primeNette),
                accessoires: displayNumbers(contrat?.accessoires),
                rawPrimeTotale: contrat?.primeTotale || 0,
                data: contrat,
            }));
            setData(ctrs);
            setPagination({ ...params.pagination, total: totalElements });
            setTotal(totalElements);
        } catch (error) {
            console.error("Failed to fetch data:", error);
        } finally {
            setLoadingContrats(false);
        }
    };

    useEffect(() => {
        const filters = {
            ...filteredInfo,
            ...(selectedDate ? { startDate: selectedDate[0], endDate: selectedDate[1] } : {}),
            compagnie: selectedCompagnies,
        };
        fetchData({ pagination: { current: currentPage, pageSize }, filters });

        axiosInstance.get(`/api/parametre/type/BANQUE`).then((res) => setBanques(res.data)).catch(console.log);

        axiosInstance.get(`/api/organisme/assurance`).then((res) => {
            const membs = res.data.map((m, i) => ({ key: i, text: m.raisonSociale, value: m.code }));
            [
                { key: "DTRF", text: "DTRF", value: "DTRF" },
                { key: "DTRF-Lome", text: "DTRF-LOME", value: "LOME" },
                { key: "DTRF-Kpalime", text: "DTRF-KPALIME", value: "KPALIME" },
                { key: "DTRF-Atakpame", text: "DTRF-ATAKPAME", value: "ATAKPAME" },
                { key: "DTRF-Sokode", text: "DTRF-SOKODE", value: "SOKODE" },
                { key: "DTRF-Kara", text: "DTRF-KARA", value: "KARA" },
                { key: "DTRF-Dapaong", text: "DTRF-DAPAONG", value: "DAPAONG" },
                { key: "STCA", text: "STCA", value: "STCA" },
            ].forEach((x) => membs.push(x));
            setMembres(membs);
        }).catch(console.log);

        axiosInstance.get(`/api/contrat/count`).then((res) => setCount(res.data)).catch(console.log);
    }, [currentPage, pageSize, selectedDate]);

    /* ── Export Excel ── */
    const exportToExcel = () => {
        const rows = filteredData.map((r, i) => ({
            "N°": i + 1,
            "N° Police": r.numeroPolice || "",
            "Souscripteur": r.souscripteur || "",
            "Date Effet": r.dateEffet || "",
            "Date Échéance": r.dateEcheance || "",
            "Prime Totale (FCFA)": r.rawPrimeTotale,
            "Statut": r.regle,
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        ws["!cols"] = [
            { wch: 5 }, { wch: 18 }, { wch: 28 }, { wch: 16 }, { wch: 16 }, { wch: 18 }, { wch: 14 },
        ];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Attente encaissement");
        XLSX.writeFile(wb, `attente_encaissement_${dayjs().format("YYYY-MM-DD")}.xlsx`);
    };

    /* ── Colonnes ── */
    const columns = [
        {
            title: "#",
            key: "index",
            width: 50,
            render: (_, __, i) => (
                <Text type="secondary" style={{ fontSize: 12 }}>
                    {(currentPage - 1) * pageSize + i + 1}
                </Text>
            ),
        },
        {
            title: "Statut",
            dataIndex: "regle",
            key: "regle",
            width: 130,
            render: (_, record) =>
                record.regle === "REGLE" ? (
                    <Tag icon={<CheckCircleOutlined />} color="success" style={{ borderRadius: 6 }}>
                        Réglé
                    </Tag>
                ) : (
                    <Tag icon={<ClockCircleOutlined />} color="warning" style={{ borderRadius: 6 }}>
                        Non réglé
                    </Tag>
                ),
            // v2 : pas de filtre (toutes les lignes sont « Non réglé »).
            ...(v2 ? {} : {
                filters: [
                    { text: "Réglé", value: "REGLE" },
                    { text: "Non réglé", value: "NON REGLE" },
                ],
                filteredValue: filteredInfo.regle || null,
                onFilter: (value, record) => record.regle === value,
            }),
        },
        {
            title: "Souscripteur",
            key: "souscripteur",
            render: (_, record) => (
                <div>
                    <Text strong style={{ fontSize: 13 }}>{record.souscripteur}</Text>
                    <div>
                        <Text type="secondary" style={{ fontSize: 11 }}>{record.numeroPolice}</Text>
                    </div>
                </div>
            ),
        },
        {
            title: "Date effet",
            dataIndex: "dateEffet",
            key: "dateEffet",
            width: 120,
            render: (v) => <Text style={{ fontSize: 12 }}>{v}</Text>,
        },
        {
            title: "Date échéance",
            dataIndex: "dateEcheance",
            key: "dateEcheance",
            width: 130,
            render: (v) => <Text style={{ fontSize: 12 }}>{v}</Text>,
        },
        {
            title: "Prime totale",
            dataIndex: "primeTotale",
            key: "primeTotale",
            width: 140,
            render: (_, record) => (
                <Text strong style={{ fontSize: 13, color: record.regle === "REGLE" ? "#389e0d" : "#d48806" }}>
                    {record.primeTotale} <span style={{ fontWeight: 400, fontSize: 11 }}>FCFA</span>
                </Text>
            ),
        },
        {
            title: "Actions",
            key: "actions",
            width: 100,
            render: (_, record) => (
                <Space size={4}>
                    {rightsService("HLA037") && record.numeroPolice && record.regle !== "REGLE" && (
                        <Tooltip title="Encaisser">
                            <Button
                                type="primary"
                                size="small"
                                icon={<CreditCardOutlined />}
                                onClick={() => { setSelectedContrat(record); setMontantEncaisse(record.rawPrimeTotale); setModalModePaiement(true); }}
                                style={{ borderRadius: 6, background: "#52c41a", borderColor: "#52c41a" }}
                            />
                        </Tooltip>
                    )}
                    <Tooltip title="Voir le contrat">
                        <Button
                            size="small"
                            icon={<SearchOutlined />}
                            onClick={() => navigate(`/details-contrat/${record.codeId}`)}
                            style={{ borderRadius: 6 }}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    /* ── Confirmation & règlement ── */
    const alertReglement = (contractToPay) => {
        confirm({
            title: "Confirmer l'encaissement",
            icon: <ExclamationCircleFilled />,
            content: (
                <div>
                    <p>Encaisser le contrat <strong>{contractToPay?.numeroPolice}</strong> ?</p>
                    <p style={{ color: "#888", fontSize: 12 }}>Toutes vos actions sont historisées.</p>
                </div>
            ),
            okText: "Confirmer",
            okType: "primary",
            cancelText: "Annuler",
            onOk() { reglementContrat(contractToPay); },
        });
    };

    const alertReglements = (cts) => {
        confirm({
            title: `Confirmer l'encaissement de ${cts.length} contrat(s) ?`,
            icon: <ExclamationCircleFilled />,
            content: "Toutes vos actions sont historisées.",
            okText: "Confirmer",
            okType: "primary",
            cancelText: "Annuler",
            onOk() { reglementsContrats(cts); },
        });
    };

    const reglementContrat = (cont) => {
        const target = cont || selectedContrat;
        // Encaissement PARTIEL (HLA093) : si le montant saisi est < prime, on transmet montantPaye.
        // Sinon (plein, ou sans le droit) → paramètre absent, comportement inchangé.
        const prime = target?.rawPrimeTotale;
        const partiel =
            canEncaissementPartiel &&
            montantEncaisse != null &&
            prime != null &&
            montantEncaisse < prime;
        const qsPartiel = partiel ? `?montantPaye=${montantEncaisse}` : "";
        if (modePaiement === "ESPECES") {
            axiosInstance
                .get(`/api/reglement/especes/contrat/codeId/${target.codeId}/${LOGGED_USER.codeId}${qsPartiel}`)
                .then(() => { toast.success("Règlement effectué avec succès !"); window.location.reload(); })
                .catch(() => toast.error("Erreur lors du règlement"));
        } else {
            axiosInstance
                .get(`/api/reglement/contrat/codeId/${target.codeId}/${LOGGED_USER.codeId}/${modePaiement}/${numeroPaiement || reference}/${banque || reference}${qsPartiel}`)
                .then(() => { toast.success("Règlement effectué avec succès !"); window.location.reload(); })
                .catch(() => toast.error("Erreur lors du règlement"));
        }
    };

    const reglementsContrats = (contrats) => {
        const allContrats = contrats.map((c) => c.data);
        if (modePaiement === "ESPECES") {
            axiosInstance
                .post(`/api/reglement/especes/contrats`, JSON.stringify(allContrats))
                .then(() => { toast.success("Règlements effectués avec succès !"); window.location.reload(); })
                .catch(() => toast.error("Erreur lors du règlement"));
        } else {
            axiosInstance
                .post(`/api/reglement/else/${modePaiement}/${numeroPaiement}/${banque}`, JSON.stringify(allContrats))
                .then(() => { toast.success("Règlements effectués avec succès !"); window.location.reload(); })
                .catch(() => toast.error("Erreur lors du règlement"));
        }
    };

    const handleDateChange = (_, dateStrings) => {
        const [s, e] = dateStrings;
        if (!s || !e) { setSelectedDate(null); return; }
        const toISO = (d) => { const [day, month, year] = d.split("/"); return `${year}-${month}-${day}`; };
        setSelectedDate([toISO(s), toISO(e)]);
        setCurrentPage(1);
    };

    const filteringOnCompanies = (companies) => {
        setSelectedCompagnies(companies);
        fetchData({
            pagination: { current: 1, pageSize },
            filters: { ...filteredInfo, compagnie: companies },
        });
    };

    const onSelectChange = (keys, rows) => {
        setSelectedRowKeys(keys);
        setSelectedRows(rows);
    };

    const rowSelection = { selectedRowKeys, onChange: onSelectChange };

    const isPoolSiege =
        LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL" &&
        (LOGGED_USER?.agence?.typeAgence === "SIEGE" || LOGGED_USER?.agence === null);

    /* ── Cartes stat ── */
    const statCards = [
        {
            label: "Total en attente",
            value: total,
            sub: "contrats",
            color: "#fa8c16",
            bg: "#fff7e6",
            icon: "bx bx-time-five",
        },
        {
            label: "Contrats actifs",
            value: count?.valideCount ?? 0,
            sub: "contrats",
            color: "#52c41a",
            bg: "#f6ffed",
            icon: "bx bx-check-circle",
        },
        {
            label: "Contrats annulés",
            value: count?.annuleCount ?? 0,
            sub: "contrats",
            color: "#ff4d4f",
            bg: "#fff1f0",
            icon: "bx bx-x-circle",
        },
        {
            label: "En proposition",
            value: count?.enPropositionCount ?? 0,
            sub: "contrats",
            color: "#1890ff",
            bg: "#e6f7ff",
            icon: "bx bx-file",
        },
    ];

    const pendingCount  = filteredData.filter((r) => r.regle !== "REGLE").length;
    const settledCount  = filteredData.filter((r) => r.regle === "REGLE").length;

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"CAISSE"} />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">

                            {/* ── Titre ── */}
                            <div style={{ marginBottom: 24 }}>
                                <Title level={4} style={{ margin: 0, color: "#242166" }}>
                                    <ClockCircleOutlined style={{ marginRight: 8 }} />
                                    {v2 ? "Primes à encaisser" : "En attente d'encaissement"}
                                </Title>
                                <Text type="secondary">
                                    {v2
                                        ? "Règlements non effectués — affaires nouvelles et avenants"
                                        : "Contrats dont le règlement n'a pas encore été encaissé"}
                                </Text>
                            </div>

                            {/* ── Cartes statistiques ── */}
                            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                                {statCards.map((s, i) => (
                                    <Col xs={24} sm={12} xl={6} key={i}>
                                        <Card
                                            style={{
                                                borderRadius: 12,
                                                border: `1px solid ${s.color}30`,
                                                background: s.bg,
                                                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                                            }}
                                            bodyStyle={{ padding: "16px 20px" }}
                                        >
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                                <div>
                                                    <Text style={{ color: "#666", fontSize: 12, display: "block", marginBottom: 4 }}>
                                                        {s.label}
                                                    </Text>
                                                    <Text strong style={{ fontSize: 22, color: s.color, display: "block" }}>
                                                        {s.value}
                                                    </Text>
                                                    <Text style={{ fontSize: 12, color: s.color }}>{s.sub}</Text>
                                                </div>
                                                <div style={{
                                                    width: 44, height: 44, borderRadius: 10,
                                                    background: `${s.color}20`,
                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                }}>
                                                    <i className={s.icon} style={{ fontSize: 22, color: s.color }} />
                                                </div>
                                            </div>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>

                            {/* ── Tableau ── */}
                            <Card
                                style={{ borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
                                bodyStyle={{ padding: "16px 20px" }}
                            >
                                {/* Barre d'outils */}
                                <Row gutter={[12, 12]} align="middle" style={{ marginBottom: 16 }}>
                                    <Col xs={24} sm={7}>
                                        <Input
                                            prefix={<SearchOutlined style={{ color: "#bbb" }} />}
                                            placeholder="Rechercher souscripteur, police..."
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            allowClear
                                            style={{ borderRadius: 8 }}
                                        />
                                    </Col>
                                    <Col xs={24} sm={7}>
                                        <ConfigProvider locale={frFR}>
                                            <RangePicker
                                                format="DD/MM/YYYY"
                                                onChange={handleDateChange}
                                                placeholder={["Date début", "Date fin"]}
                                                disabledDate={(c) => c && c > dayjs().endOf("day")}
                                                style={{ width: "100%", borderRadius: 8 }}
                                            />
                                        </ConfigProvider>
                                    </Col>
                                    {isPoolSiege && (
                                        <Col xs={24} sm={5}>
                                            <Select
                                                mode="multiple"
                                                placeholder="Filtre compagnie"
                                                onChange={filteringOnCompanies}
                                                options={membres.map((m) => ({ label: m.text, value: m.value, key: m.value }))}
                                                style={{ width: "100%", borderRadius: 8 }}
                                                maxTagCount={1}
                                            />
                                        </Col>
                                    )}
                                    <Col xs={24} sm={isPoolSiege ? 5 : 10} style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                                        <Tooltip title="Activer la sélection multiple pour encaissement groupé">
                                            <Space>
                                                <Text style={{ fontSize: 12, color: "#666" }}>Sélection</Text>
                                                <Switch
                                                    size="small"
                                                    checked={selecting}
                                                    onChange={setSelecting}
                                                />
                                            </Space>
                                        </Tooltip>
                                        <Button
                                            icon={<FileExcelOutlined />}
                                            onClick={exportToExcel}
                                            style={{
                                                borderRadius: 8,
                                                background: "#217346",
                                                borderColor: "#217346",
                                                color: "#fff",
                                                fontWeight: 500,
                                            }}
                                        >
                                            Excel
                                        </Button>
                                    </Col>
                                </Row>

                                {/* Indicateurs rapides */}
                                <Row gutter={8} style={{ marginBottom: 12 }}>
                                    <Col>
                                        <Tag color="warning" icon={<ClockCircleOutlined />}>
                                            {pendingCount} non réglé{pendingCount > 1 ? "s" : ""}
                                        </Tag>
                                    </Col>
                                    <Col>
                                        <Tag color="success" icon={<CheckCircleOutlined />}>
                                            {settledCount} réglé{settledCount > 1 ? "s" : ""}
                                        </Tag>
                                    </Col>
                                    {selecting && selectedRows.length > 0 && (
                                        <Col>
                                            <Button
                                                type="primary"
                                                size="small"
                                                icon={<CreditCardOutlined />}
                                                onClick={() => {
                                                    const nonRegle = selectedRows.filter((r) => r.regle !== "REGLE");
                                                    if (nonRegle.length === 0) {
                                                        toast.info("Tous les contrats sélectionnés sont déjà réglés");
                                                        return;
                                                    }
                                                    setContractsToPay(nonRegle);
                                                    setModalModePaiement(true);
                                                }}
                                                style={{ borderRadius: 6, background: "#52c41a", borderColor: "#52c41a" }}
                                            >
                                                Encaisser la sélection ({selectedRows.filter((r) => r.regle !== "REGLE").length})
                                            </Button>
                                        </Col>
                                    )}
                                </Row>

                                <ConfigProvider locale={frFR}>
                                    <Spin spinning={loadingContrats}>
                                        <Table
                                            rowSelection={selecting ? rowSelection : null}
                                            columns={columns}
                                            rowKey="codeId"
                                            dataSource={filteredData}
                                            onChange={(pag, filters, sorter) => {
                                                setCurrentPage(pag.current);
                                                setPageSize(pag.pageSize);
                                                setPagination(pag);
                                                setFilteredInfo(filters);
                                                setSortedInfo(sorter);
                                            }}
                                            pagination={{
                                                current: currentPage,
                                                pageSize,
                                                total,
                                                showSizeChanger: true,
                                                showTotal: (t) => `${t} contrats`,
                                                pageSizeOptions: ["10", "20", "50"],
                                            }}
                                            onRow={(record) => ({
                                                style: {
                                                    background: record.regle === "REGLE"
                                                        ? STATUS_COLORS.regle.bg
                                                        : STATUS_COLORS.nonRegle.bg,
                                                    borderLeft: `3px solid ${record.regle === "REGLE"
                                                        ? STATUS_COLORS.regle.border
                                                        : STATUS_COLORS.nonRegle.border}`,
                                                    transition: "all 0.2s",
                                                },
                                            })}
                                            size="middle"
                                        />
                                    </Spin>
                                </ConfigProvider>
                            </Card>
                        </div>

                        <Footer />
                        <div className="content-backdrop fade" />
                    </div>
                </div>
            </div>

            {/* ── Modal mode de paiement ── */}
            <Modal
                title={
                    <Space>
                        <CreditCardOutlined />
                        <span>Mode de paiement</span>
                    </Space>
                }
                centered
                open={modalModePaiement}
                onOk={() =>
                    selecting && contractsToPay.length > 0
                        ? alertReglements(contractsToPay)
                        : alertReglement(selectedContrat)
                }
                onCancel={() => setModalModePaiement(false)}
                okText="Encaisser"
                cancelText="Annuler"
                okButtonProps={{ style: { background: "#52c41a", borderColor: "#52c41a" } }}
            >
                <Form form={modePaiementForm} layout="vertical">
                    <Form.Item
                        name="modePaiement"
                        label="Mode de paiement"
                        rules={[{ required: true, message: "Veuillez sélectionner un mode" }]}
                    >
                        <Select
                            onChange={(v) => setModePaiement(v)}
                            placeholder="Sélectionner le mode de paiement"
                            style={{ borderRadius: 8 }}
                        >
                            <Option value="ESPECES">Espèces</Option>
                            <Option value="BANQUE">Banque (Chèque)</Option>
                            <Option value="VIREMENT">Banque (Virement)</Option>
                            <Option value="MOBILE_MONEY">Mixx By Yas / Moov Money</Option>
                        </Select>
                    </Form.Item>
                    {/* Encaissement PARTIEL (HLA093) — uniquement pour un contrat seul (pas le groupé). */}
                    {canEncaissementPartiel && !(selecting && contractsToPay.length > 0) && selectedContrat && (
                        <Form.Item
                            label="Montant encaissé"
                            help={
                                montantEncaisse != null &&
                                selectedContrat.rawPrimeTotale != null &&
                                montantEncaisse < selectedContrat.rawPrimeTotale
                                    ? `Encaissement partiel — reste à payer : ${displayNumbers(
                                          selectedContrat.rawPrimeTotale - montantEncaisse
                                      )} FCFA`
                                    : "Prime totale (encaissement plein). Diminuez le montant pour un encaissement partiel."
                            }
                        >
                            <InputNumber
                                value={montantEncaisse}
                                onChange={(v) => setMontantEncaisse(v)}
                                min={0}
                                max={selectedContrat.rawPrimeTotale}
                                style={{ width: "100%", borderRadius: 8 }}
                                formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
                                parser={(v) => v.replace(/\s/g, "")}
                                addonAfter="FCFA"
                            />
                        </Form.Item>
                    )}
                    {(modePaiement === "BANQUE" || modePaiement === "MOBILE_MONEY") && (
                        <Form.Item
                            name="numeroPaiement"
                            label={modePaiement === "BANQUE" ? "Numéro de chèque" : "Numéro Flooz / TMoney"}
                            rules={[{ required: true, message: "Veuillez renseigner ce champ" }]}
                        >
                            <Input
                                placeholder={modePaiement === "BANQUE" ? "Numéro du chèque" : "Numéro de téléphone"}
                                onChange={(e) => setNumeroPaiement(e.target.value)}
                                style={{ borderRadius: 8 }}
                            />
                        </Form.Item>
                    )}
                    {(modePaiement === "BANQUE" || modePaiement === "VIREMENT") && (
                        <Form.Item
                            name="banque"
                            label="Banque"
                            rules={[{ required: true, message: "Veuillez sélectionner la banque" }]}
                        >
                            <Select
                                onChange={(v) => setBanque(v)}
                                placeholder="Sélectionner la banque"
                                style={{ borderRadius: 8 }}
                            >
                                {banques.map((b) => (
                                    <Option key={b.parametreId} value={b.libelleParametre}>
                                        {b.libelleParametre}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}
                    {(modePaiement === "MOBILE_MONEY" || modePaiement === "VIREMENT") && (
                        <Form.Item
                            name="referencePaiement"
                            label="Référence du paiement"
                            rules={[{ required: true, message: "Veuillez renseigner la référence" }]}
                        >
                            <Input
                                placeholder="Numéro de référence"
                                onChange={(e) => setReference(e.target.value)}
                                style={{ borderRadius: 8 }}
                            />
                        </Form.Item>
                    )}
                    {modePaiement === "VIREMENT" && (
                        <Form.Item
                            name="dateVirement"
                            label="Date du virement"
                            rules={[{ required: true, message: "Veuillez sélectionner la date" }]}
                        >
                            <ConfigProvider locale={frFR}>
                                <DatePicker
                                    placeholder="Date du virement"
                                    onChange={(_, v) => setDateVirement(v)}
                                    style={{ width: "100%", borderRadius: 8 }}
                                    disabledDate={(c) => c && c > dayjs().endOf("day")}
                                />
                            </ConfigProvider>
                        </Form.Item>
                    )}
                </Form>
            </Modal>

            <div className="layout-overlay layout-menu-toggle" />
            <div className="drag-target" />
        </div>
    );
};

export default EnAttenteDEncaissement;
